import { useDispatch } from "react-redux";
import { useState } from "react";
import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";
import { newAgents } from "../../../RTK/agents/AddNewAgent";
import { fetchAgents } from "../../../RTK/agents/GetAgents";

function NewAgentModal({ onClose, onSuccess }) {
  const dispatch = useDispatch();
  const [formError, setFormError] = useState({ image: false, fields: false });
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  
  const [formData, setFormData] = useState({
    icon: null,
    name: "",
    channel: "",
    contact_email: "",
    website: "",
    api_token: "",
    api_url: "",
    agent_balance: "",
  });
  const [imagePreview, setImagePreview] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "agent_balance") {
      // Only allow numbers
      const numberValue = value.replace(/[^0-9]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: numberValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setFormError((prev) => ({ ...prev, image: true }));
        return;
      }
      setFormData((prev) => ({
        ...prev,
        icon: file,
      }));
      setImagePreview(URL.createObjectURL(file));
      setFormError((prev) => ({ ...prev, image: false }));
    }
  };

  const validateForm = () => {
    const requiredFields = [
      "name",
      "channel",
      "contact_email",
      "website",
      "api_token",
      "api_url",
      "agent_balance",
    ];

    const hasEmptyFields = requiredFields.some((field) => !formData[field]);
    const hasNoImage = !formData.icon;
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      formData.contact_email
    );

    setFormError({
      fields: hasEmptyFields,
      image: hasNoImage,
      email: !isValidEmail,
    });

    return !hasEmptyFields && !hasNoImage && isValidEmail;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors before submitting.");
      return;
    }

    setIsLoading(true);

    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      formDataToSend.append(key, formData[key]);
    });

    try {
      await dispatch(newAgents(formDataToSend)).unwrap();
      await dispatch(fetchAgents()).unwrap(); // Refresh the agents list
      toast.success("Agent added successfully!");
      if (onSuccess) {
        onSuccess(); // Call the success callback
      }
      onClose(); // Close the modal
    } catch (error) {
      const errorMessage =
        error?.message || error?.response?.data?.message || "Failed to add agent.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    isOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-80 z-[5000] flex items-center justify-center">
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg z-[5500] w-full max-w-md text-white">
          <h2 className="text-xl font-bold mb-4">إضافة وكيل جديد</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col items-center bg-[#2a2a2a] p-4 rounded">
              <div className="img-input flex items-center gap-2 py-2">
                <img
                  src={imagePreview || "avatar.png"}
                  alt="Preview"
                  className="w-[60px] h-[60px] rounded-full border border-[#FEC111] object-cover"
                />
                <label
                  htmlFor="file"
                  className="cursor-pointer border-b border-[#FEC111] text-[#FEC111] font-semibold"
                >
                  Upload Image
                </label>
              </div>
              <input
                type="file"
                id="file"
                name="icon"
                className="hidden"
                accept="image/*"
                onChange={handleImageChange}
              />
              {formError.image && (
                <p className="text-red-500 text-sm mt-2">
                  Please upload a valid image.
                </p>
              )}
            </div>

            {[
              "name",
              "channel",
              "contact_email",
              "website",
              "api_token",
              "api_url",
              "agent_balance",
            ].map((field, index) => (
              <input
                key={index}
                type={field === "contact_email" ? "email" : "text"}
                name={field}
                value={formData[field]}
                onChange={handleChange}
                placeholder={field.replace("_", " ").toUpperCase()}
                className="w-full px-3 py-2 rounded-md bg-gray-700 border border-gray-600 text-white"
                required
              />
            ))}

            {formError.fields && (
              <p className="text-red-500 text-sm">
                Please fill in all required fields.
              </p>
            )}

            <div className="flex justify-between space-x-2">
              <button
                type="button"
                disabled={isLoading}
                onClick={() => setIsOpen(false)}
                className="px-4 py-2 rounded-md bg-gray-600 hover:bg-gray-500 disabled:opacity-50"
              >
                إلغاء
              </button>

              <button
                type="submit"
                disabled={isLoading}
                className={`px-4 py-2 rounded-md text-black bg-yellow-600 hover:bg-yellow-500 
              disabled:opacity-50 flex items-center gap-2 ${
                isLoading ? "cursor-not-allowed" : ""
              }`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin cursor-not-allowed" />
                    جاري الحفظ...
                  </>
                ) : (
                  "حفظ"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );
}

export default NewAgentModal;
